import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";
import { z } from "zod";
import { holderEvidenceSchema } from "./community-holders-v1";

/**
 * Public wire contract of the community identity service (`apps/community`) used by `apps/web`.
 * A person proves wallets by signature, links one X account and optionally one Discord account,
 * and earns referral credit for sign-ups through their referral link.
 */

const dateTimeSchema = z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN);
/** Lowercase 20-byte hex address as the service returns it. */
export const communityAddressSchema = z.string().regex(/^0x[0-9a-f]{40}$/);
const addressInputSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/);
const hexSchema = z.string().regex(/^0x(?:[0-9a-fA-F]{2})+$/);
const decimalSchema = z.string().regex(/^(0|[1-9][0-9]*)$/);
/** Referral codes are 8 characters from an unambiguous lowercase alphabet. */
export const referralCodeSchema = z.string().regex(/^[23456789abcdefghjkmnpqrstuvwxyz]{8}$/);
export const COMMUNITY_NONCE_PATTERN = /^[0-9a-f]{32}$/;

export const communityChallengeRequestSchema = z.object({
  address: addressInputSchema,
  /** Chain the wallet is connected to; contract wallets are verified on this chain. */
  chainId: z.number().int().positive().max(2_147_483_647),
  /** Single-use human verification for sessionless challenges; omitted for authenticated wallet linking. */
  turnstileToken: z.string().max(2_048).optional(),
}).strict();

export const communityChallengeResponseSchema = z.object({
  nonce: z.string().regex(COMMUNITY_NONCE_PATTERN),
  /** The exact EIP-4361 message the wallet must sign. */
  message: z.string().min(1).max(2_000),
  expiresAt: dateTimeSchema,
}).strict();

export const communityVerifyRequestSchema = z.object({
  nonce: z.string().regex(COMMUNITY_NONCE_PATTERN),
  /** EOA signatures are 65 bytes; contract-wallet (ERC-1271/6492) signatures may be longer. */
  signature: hexSchema.refine((value) => value.length <= 2 + 2 * 16_384, "signature too long"),
  /** Referral code captured from `?r=`; applied only when this signature creates a new person. */
  referralCode: referralCodeSchema.optional(),
}).strict();

/** Genesis mints on Ethereum mainnet: allowlist entries go to a wallet that is its owner's there. */
export const COMMUNITY_MINT_CHAIN_ID = 1;

export const communityWalletSchema = z.object({
  address: communityAddressSchema,
  kind: z.enum(["eoa", "contract"]),
  /**
   * Chain the linking signature was verified on. A contract wallet (Safe, Abstract Global Wallet)
   * is its owner's only there. Optional so a client can ship before the service sends it.
   */
  chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().optional(),
  linkedAt: dateTimeSchema,
}).strict();

/**
 * Whether a wallet can receive allowlist entries: any key wallet, or a contract wallet proven on
 * the mint chain. A wallet without `chainId` is judged by kind alone.
 */
export function isMintWalletEligible(wallet: { readonly kind: "eoa" | "contract"; readonly chainId?: number | undefined }): boolean {
  return wallet.kind === "eoa" || wallet.chainId === COMMUNITY_MINT_CHAIN_ID;
}

/** An X profile picture on X's image CDN (400x400); `null` when none was read. */
export const communityXProfileImageSchema = z.string().max(512).regex(/^https:\/\/(pbs|abs)\.twimg\.com\//).nullable();

export const communityXAccountSchema = z.object({
  userId: decimalSchema,
  username: z.string().min(1).max(64),
  name: z.string().max(128),
  profileImageUrl: communityXProfileImageSchema,
  linkedAt: dateTimeSchema,
}).strict();

export const communityDiscordAccountSchema = z.object({
  userId: decimalSchema,
  username: z.string().min(1).max(64),
  linkedAt: dateTimeSchema,
}).strict();

export const communityHoldingSchema = z.object({
  chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  collection: communityAddressSchema,
  /** Decimal ERC-1155 token id; `null` for an ERC-721 collection. */
  tokenId: decimalSchema.nullable(),
  label: z.string().max(128),
  /** Summed across every linked wallet at the latest holdings snapshot. */
  balance: decimalSchema,
}).strict();

export const communityProfileSchema = z.object({
  personId: z.guid(),
  createdAt: dateTimeSchema,
  wallets: z.array(communityWalletSchema).min(1),
  /** Wallet that receives allowlist entries; defaults to the first mint-eligible linked wallet. */
  mintWallet: communityAddressSchema,
  x: communityXAccountSchema.nullable(),
  discord: communityDiscordAccountSchema.nullable(),
  referral: z.object({
    code: referralCodeSchema,
    /** Referred people who have linked a wallet and an X account and are not disqualified. */
    qualified: z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative(),
    /** Referred people who have not yet linked an X account. */
    pending: z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative(),
    /** Genesis Aura rank in the latest snapshot; `null` while not eligible or not yet in a snapshot. */
    rank: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  }).strict(),
  holdings: z.array(communityHoldingSchema),
  /** When the holdings were read; `null` before the first snapshot that included this person. */
  holdingsObservedAt: dateTimeSchema.nullable(),
  holderEvidence: z.array(holderEvidenceSchema),
}).strict();

export const communitySessionResponseSchema = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  expiresAt: dateTimeSchema,
  profile: communityProfileSchema,
}).strict();

export const communityMintWalletRequestSchema = z.object({
  address: addressInputSchema,
}).strict();

export const communityDiscordStartRequestSchema = z.object({
  /** Single-use ticket from the Discord `/link` command; binds the expected Discord account. */
  ticket: z.string().regex(/^[A-Za-z0-9_-]{43}$/).optional(),
}).strict();

export const communityOAuthStartResponseSchema = z.object({
  /** Top-level navigation target; the provider returns to the service, which redirects to the link flow. */
  authorizeUrl: z.string().url(),
}).strict();

/** X may recover a previously committed link without another provider consent round trip. */
export const communityXStartResponseSchema = z.union([
  communityOAuthStartResponseSchema,
  z.object({ linked: z.literal(true), profile: communityProfileSchema.extend({ x: communityXAccountSchema }) }).strict(),
]);

/** The web app hosts the link flow on the Genesis campaign entry route. */
export const COMMUNITY_LINK_PATH = "/genesis/me";

/** `${webOrigin}/genesis/me`: where OAuth callbacks and Discord `/link` buttons send people. */
export function communityLinkUrl(webOrigin: string | URL): URL {
  return new URL(COMMUNITY_LINK_PATH, webOrigin);
}

/**
 * A personal invitation: the Genesis arrival page for `code`, so a visitor sees who invited them and
 * what joining means before signing up. Already-issued root links are still captured, because `?r=`
 * is read on every route.
 */
export function referralSignupUrl(webOrigin: string | URL, code: string): URL {
  return new URL(`/genesis/i/${referralCodeSchema.parse(code)}`, webOrigin);
}

/**
 * OAuth callbacks never link. A proven provider account is held as a pending link and the callback
 * redirects to the link flow ({@link communityLinkUrl}) with `&provider=<x|discord>&pending=<code>`; the
 * web app, holding the session that started the authorization, confirms it with
 * `POST /auth/<provider>/complete`. A failed callback redirects there with `&error=<code>`.
 */
export const communityLinkedProviderSchema = z.enum(["x", "discord"]);
export const communityLinkCompleteRequestSchema = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
}).strict();
/**
 * Callback (`?error=`) codes. `POST /auth/<provider>/complete` answers 200 with the profile, or
 * 410 `link_expired`, 403 `link_session_mismatch` (signed in as someone other than the person who started
 * the authorization), 403 `x_not_eligible` (the X account cannot join Genesis; deliberately says
 * nothing about why, and nothing is linked), or 409 with one of the `*_linked_elsewhere` /
 * `*_already_linked` codes below.
 */
export const communityLinkErrorSchema = z.enum([
  "oauth_denied",
  "oauth_failed",
  "state_expired",
  "x_linked_elsewhere",
  "x_already_linked",
  "x_not_eligible",
  "discord_linked_elsewhere",
  "discord_already_linked",
  "discord_ticket_mismatch",
]);

export const communityFunnelStepSchema = z.enum([
  "link_prompt_shown",
  "link_prompt_opened",
  "wallet_connect",
  "wallet_sign",
  "x_link",
  "discord_link",
  "share",
  "open_in_browser",
  // Genesis campaign arrival funnel.
  "genesis_view",
  "invite_open",
  "join_click",
]);
export const communityFunnelOutcomeSchema = z.enum([
  "shown",
  "started",
  "succeeded",
  "failed",
  "cancelled",
]);
export const communityInAppBrowserSchema = z.enum([
  "none",
  "x",
  "discord",
  "telegram",
  "instagram",
  "facebook",
  "wallet",
  "other",
]);

const shortLabel = z.string().min(1).max(64);

export const communityFunnelEventSchema = z.object({
  step: communityFunnelStepSchema,
  outcome: communityFunnelOutcomeSchema,
  /** Bounded machine code for failures, e.g. `user_rejected`, `chain_mismatch`. */
  errorCode: z.string().regex(/^[a-z0-9_]{1,48}$/).optional(),
  wallet: z.object({
    /** EIP-6963 provider name or WalletConnect peer name. */
    name: shortLabel.optional(),
    /** EIP-6963 reverse-DNS identifier, e.g. `io.metamask`. */
    rdns: z.string().regex(/^[a-z0-9.-]{1,96}$/).optional(),
    connectorId: shortLabel.optional(),
  }).strict().optional(),
  env: z.object({
    os: z.enum(["ios", "android", "macos", "windows", "linux", "chromeos", "other"]),
    browser: z.enum(["chrome", "safari", "firefox", "edge", "brave", "opera", "samsung", "other"]),
    device: z.enum(["mobile", "tablet", "desktop"]),
    inApp: communityInAppBrowserSchema,
  }).strict(),
}).strict();

export const communityFunnelEventsRequestSchema = z.object({
  /** Random per-browser identifier kept in local storage; not linked to a person unless signed in. */
  clientId: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
  events: z.array(communityFunnelEventSchema).min(1).max(20),
}).strict();

export const communityAllowlistProofResponseSchema = z.object({
  roundId: z.guid(),
  name: z.string().min(1).max(128),
  chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  collection: communityAddressSchema,
  address: communityAddressSchema,
  /** The whitelist buying limit in the leaf; `null` for formats without one. */
  entryLimit: z.number().int().min(1).max(10).nullable(),
  proof: z.array(z.string().regex(/^0x[0-9a-f]{64}$/)).max(64),
}).strict();

export const communityErrorResponseSchema = z.object({
  error: z.string().regex(/^[a-z0-9_]{1,64}$/),
}).strict();

export type CommunityChallengeRequest = z.infer<typeof communityChallengeRequestSchema>;
export type CommunityChallengeResponse = z.infer<typeof communityChallengeResponseSchema>;
export type CommunityVerifyRequest = z.infer<typeof communityVerifyRequestSchema>;
export type CommunityWallet = z.infer<typeof communityWalletSchema>;
export type CommunityHolding = z.infer<typeof communityHoldingSchema>;
export type CommunityProfile = z.infer<typeof communityProfileSchema>;
export type CommunitySessionResponse = z.infer<typeof communitySessionResponseSchema>;
export type CommunityOAuthStartResponse = z.infer<typeof communityOAuthStartResponseSchema>;
export type CommunityXStartResponse = z.infer<typeof communityXStartResponseSchema>;
export type CommunityLinkError = z.infer<typeof communityLinkErrorSchema>;
export type CommunityFunnelEvent = z.infer<typeof communityFunnelEventSchema>;
export type CommunityFunnelEventsRequest = z.infer<typeof communityFunnelEventsRequestSchema>;
export type CommunityAllowlistProofResponse = z.infer<typeof communityAllowlistProofResponseSchema>;
